/**
 * The export and deletion requests (mission 10 WP15 T084; FR-011, FR-013, FR-017; D.6, D.8, A.3;
 * contracts/tenant-lifecycle-api.md).
 *
 * - `requestExport` posts `{}`: 202 for a new request, 200 with the open one (one open request per tenant).
 *   `fulfilment_days` is the period `exportFulfilment` states (C-013).
 * - `requestDeletion` sends the typed legal name and the password, key by key. A wrong password is the 403
 *   `reauthentication-failed` (D.6), never a 401, so the session handler does not end the session for a
 *   typo; a mismatched name is the 400 `validation-failed` naming `legal_name`. Neither value reaches an
 *   Error: `ApiError`'s message is the problem title (S11). On the 202 the server has cleared the session
 *   cookies (`signed_out`); `useDeleteAccountMutation` clears the cache and leaves for `/login`.
 */
import { api } from '../api'
import type { ExportRequest } from './retention'

/** POST /v1/tenant/export's 202 or 200 (FR-011). */
export interface ExportResult {
  request: ExportRequest
  fulfilment_days: number
}

/** The request member of POST /v1/tenant/delete's 202 (FR-013). */
export interface DeletionRequest {
  id: string
  state: string
  requested_at: string
  due_at: string
  subscription_cancelled: boolean
  queued_runs_cancelled: number
}

/** POST /v1/tenant/delete's 202. */
export interface DeletionResult {
  request: DeletionRequest
  fulfilment_days: number
  signed_out: boolean
}

/** POST /v1/tenant/delete's body: the typed legal name and the password (both required). */
export interface DeletionBody {
  legal_name: string
  password: string
}

const post = (body: object): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })

export async function requestExport(): Promise<ExportResult> {
  return api<ExportResult>('/tenant/export', post({}))
}

export async function requestDeletion(body: DeletionBody): Promise<DeletionResult> {
  const clean: DeletionBody = { legal_name: body.legal_name, password: body.password }
  return api<DeletionResult>('/tenant/delete', post(clean))
}
